<template>
  <div class="container__left">
      <div class="canvas-toolbar canvas-toolbar--left panzoom-exclude">
        <div class="canvas-tool-group">
          <button id="zoomin" type="button" class="canvas-icon-btn" title="Zoom in"><i class="fa fa-plus" aria-hidden="true"></i></button>
          <button id="zoomout" type="button" class="canvas-icon-btn" title="Zoom out"><i class="fa fa-minus" aria-hidden="true"></i></button>
          <button type="button" class="canvas-icon-btn" title="Fit to screen" @click="fitToScreen"><i class="fa fa-arrows-alt" aria-hidden="true"></i></button>
        </div>
        <div class="canvas-tool-group canvas-search" @keydown.down.prevent="moveStageResult(1)" @keydown.up.prevent="moveStageResult(-1)">
          <i class="fa fa-search canvas-search__icon" aria-hidden="true"></i>
          <input
            v-model="stageQuery"
            type="text"
            class="canvas-search__input"
            placeholder="Find stage or message"
            aria-label="Find stage or message"
            data-nv-ignore-dirty
            @focus="openStageSearch"
            @blur="closeStageSearch"
            @keydown.enter.prevent="stageResults[stageResultIndex] && goToStage(stageResults[stageResultIndex].name)"
            @keydown.esc="stageQuery = ''">
          <ul v-if="stageSearchOpen && stageQuery" class="canvas-search__results">
            <li
              v-for="(result, i) in stageResults"
              :key="result.name"
              class="canvas-search__result"
              :class="{ 'canvas-search__result--active': i === stageResultIndex }"
              :title="result.name"
              @mousedown.prevent="goToStage(result.name)"
              @mouseenter="stageResultIndex = i">
              <div class="canvas-search__name">{{ result.name }}</div>
              <div v-if="result.snippet" class="canvas-search__snippet">
                <span class="canvas-search__where">{{ result.where }}</span>
                {{ result.snippet.before }}<mark>{{ result.snippet.match }}</mark>{{ result.snippet.after }}
              </div>
            </li>
            <li v-if="!stageResults.length" class="canvas-search__result canvas-search__result--empty">No stage, message or PF matches "{{ stageQuery }}"</li>
          </ul>
        </div>
        <div class="canvas-tool-group">
          <label class="canvas-toggle" :class="{ 'canvas-toggle--on': toggleOptionLabelSwitch }" title="Show the option text on each arrow">
            <input type="checkbox" v-model="toggleOptionLabelSwitch" @change="handleOptionLabelToggle">
            <span class="canvas-toggle__track"><span class="canvas-toggle__thumb"></span></span>
            Options
          </label>
          <label class="canvas-toggle" :class="{ 'canvas-toggle--on': toggleMessagesSwitch }" title="Show each stage's messages, PF, recommendations, delays, nudges and other settings inside the stage">
            <input type="checkbox" v-model="toggleMessagesSwitch" @change="handleMessagesToggle">
            <span class="canvas-toggle__track"><span class="canvas-toggle__thumb"></span></span>
            Messages
          </label>
          <label v-show="this.$route.query.app" class="canvas-toggle" :class="{ 'canvas-toggle--on': toggleTrafficSwitch }" title="How many users moved between stages. Traffic data starts from 16-Mar-2022.">
            <input type="checkbox" v-model="toggleTrafficSwitch" @change="handleTrafficToggle">
            <span class="canvas-toggle__track"><span class="canvas-toggle__thumb"></span></span>
            Traffic
          </label>
        </div>
      </div>
      <div class="canvas-toolbar canvas-toolbar--right panzoom-exclude">
        <div class="canvas-export" @focusout="closeExportMenu">
          <button type="button" class="canvas-btn" :class="{ 'canvas-btn--active': exportMenuOpen }" title="Download the graph as it's shown now" :disabled="exporting" @click="exportMenuOpen = !exportMenuOpen">
            <i class="fa" :class="exporting ? 'fa-circle-o-notch fa-spin' : 'fa-download'" aria-hidden="true"></i> Export
          </button>
          <ul v-if="exportMenuOpen" class="canvas-export__menu">
            <li><button type="button" @click="exportGraph('html')"><i class="fa fa-globe" aria-hidden="true"></i> Interactive HTML</button></li>
            <li><button type="button" @click="exportGraph('png')"><i class="fa fa-file-image-o" aria-hidden="true"></i> PNG image</button></li>
            <li><button type="button" @click="exportGraph('svg')"><i class="fa fa-file-code-o" aria-hidden="true"></i> SVG (scalable)</button></li>
          </ul>
        </div>
        <button id="legend" type="button" class="canvas-btn" :class="{ 'canvas-btn--active': showLegend }" title="Show legend" @click="showLegend = !showLegend">
          <i class="fa fa-info-circle" aria-hidden="true"></i> Legend
        </button>
        <button id="validateconversation" type="button" class="canvas-btn canvas-btn--primary" :title="'Validate & Save ' + conversationType" @click="validateConversation">
          <i class="fa fa-check-circle" aria-hidden="true"></i> Validate &amp; Save
        </button>
      </div>
      <!-- The canvas zooms on wheel and cancels the scroll; stop the wheel here so the legend scrolls. -->
      <div class="conversation-graph-legend panzoom-exclude" v-show="showLegend" @wheel.stop>
        <div class="conversation-graph-legend__header">
          <span>Legend</span>
          <button type="button" class="canvas-icon-btn canvas-icon-btn--plain" title="Close legend" @click="showLegend = false"><i class="fa fa-times" aria-hidden="true"></i></button>
        </div>
        <div id="conversationLegend"></div>
      </div>
      <div class="canvas-hint panzoom-exclude">
        Double-click a stage to open it · Right-click to edit or delete · Drag to move · Scroll to zoom
      </div>
      <div v-if="clickHint" class="canvas-click-hint" :style="{ left: clickHint.x + 'px', top: clickHint.y + 'px' }">
        <i class="fa fa-mouse-pointer" aria-hidden="true"></i> Double-click to open
      </div>
      <div v-if="drawingVisible" class="canvas-status panzoom-exclude">
        <i class="fa fa-circle-o-notch fa-spin" aria-hidden="true"></i> Drawing graph…
      </div>
      <div v-if="layoutError" class="canvas-error panzoom-exclude">
        <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
        <div>
          <strong>Couldn't draw this graph</strong>
          <p>It may be too large for the graph engine. You can still use Find stage to open any stage.</p>
        </div>
      </div>
      <div class='conversation-tree-container' :class="{ 'conversation-tree-container--stale': drawingVisible }" id="conversationTreeContainer">
        <div id="conversationTreeSvg" @contextmenu.prevent="handleRightClick($event)" @mousedown="rememberPointer" @click="handleStageClick" @dblclick="handleStageDoubleClick"></div>
      </div>

    <BlockUI v-if="processing"><i class="fa fa-cog fa-spin fa-3x fa-fw"></i><br/>Please wait...</BlockUI>
  </div>
</template>

<script>
import { layoutGraph } from './graphLayout'
import { buildGraphHtml } from './graphHtmlExport'
import Panzoom from '../../../static/plugins/panzoom/index'
import api from '../../../utils/api'
import { styleType, colorType, shapeType, fontColorType, borderColourType, arrowShape, shapePeripheries, fontSizeType, methodsGradientStart, graphDefaults } from './stylingForTree'
import { stageTypes } from '../conversation/constant'
import { sortStagesForJor } from '../../../utils/utilities'

export default {
  name: 'conversation-tree-view',
  props: ['conversationObject', 'isJourneyConversation', 'selectedStageName'],
  components: {},
  data () {
    return {
      dots: '',
      toggleOptionLabelSwitch: false,
      toggleTrafficSwitch: false,
      toggleMessagesSwitch: false,
      functionAliases: null, // function_name -> alias, loaded the first time Messages is turned on
      methodNames: null, // recommendation method shortname -> name, loaded with the aliases
      trafficData: [],
      trafficStalledStages: [],
      totalUserCount: 0,
      isFirstTimeRender: true,
      processing: false,
      showLegend: false,
      legendDrawn: false,
      drawId: 0,
      drawingVisible: false,
      clickHint: null, // { x, y } of the "Double-click to open" tip after a single click on a stage
      clickHintTimer: null,
      pointerDown: null,
      layoutError: false,
      panzoom: {},
      stageSearchIndex: [], // { name, message, pf } per stage, for Find
      exportMenuOpen: false,
      exporting: false,
      stageQuery: '',
      stageSearchOpen: false,
      stageResultIndex: 0,
      legend: `digraph {
        rankdir=LR
        bgcolor="transparent"
        ${graphDefaults}
        node [shape=plaintext]
          key [label=<<table border="0" cellpadding="2" cellspacing="0" cellborder="0">
            <tr><td port="i1">Arrow pointing to Normal stage</td></tr>
            <tr><td port="i2">Arrow pointing to Unconfigured stage</td></tr>
            <tr><td port="i3">Arrow pointing to End Journey stage</td></tr>
            </table>> fontname="Helvetica" fontcolor="#2D3748" fontsize="13"]
          key2 [label=<<table border="0" cellpadding="2" cellspacing="0" cellborder="0">
            <tr><td port="i1">&nbsp;</td></tr>
            <tr><td port="i2">&nbsp;</td></tr>
            <tr><td port="i3">&nbsp;</td></tr>
            <tr><td port="i4">&nbsp;</td></tr>
            </table>>]
          key:i1:e -> key2:i1:w [arrowhead="${arrowShape['startConversation']}"]
          key:i2:e -> key2:i2:w [arrowhead="${arrowShape['notExist']}"]
          key:i3:e -> key2:i3:w [arrowhead="${arrowShape['endConversation']}"]
          end [shape="${shapeType['endConversation']}" color="${borderColourType['endConversation']}" fontcolor="${fontColorType['endConversation']}" fontname="Helvetica" style="${styleType['endConversation']}" label="End stage" fillcolor="${colorType['endConversation']}"]
          conditionalBranching [shape="${shapeType['normalConditionalBranching']}" color="${borderColourType['normalConditionalBranching']}" fontcolor="${fontColorType['normalConditionalBranching']}"  fontname="Helvetica" style="${styleType['normalConditionalBranching']}"  label="Conditional Branching Stage" fillcolor="${colorType['normalConditionalBranching']}"]
          joR [shape="${shapeType['normalJoR']}" color="${borderColourType['normalJoR']}" fontcolor="${fontColorType['normalJoR']}" fontname="Helvetica" style="${styleType['normalJoR']}" label="Journey Of Reminder stage" fillcolor="${colorType['normalJoR']}"]
          fbOptIn [shape="${shapeType['normalFbOptin']}" color="${borderColourType['normalFbOptin']}" fontcolor="${fontColorType['normalFbOptin']}" fontname="Helvetica" style="${styleType['normalFbOptin']}" label="Fb Optin stage" fillcolor="${colorType['normalFbOptin']}"]
          drillDown [shape="${shapeType['normalDrillDown']}" color="${borderColourType['normalDrillDown']}" fontcolor="${fontColorType['normalDrillDown']}"  fontname="Helvetica" style="${styleType['normalDrillDown']}"  label="Drill Down Stage" fillcolor="${colorType['normalDrillDown']}"]
          embedded [shape="${shapeType['normalEmbeddedApp']}" color="${borderColourType['normalEmbeddedApp']}" fontcolor="${fontColorType['normalEmbeddedApp']}" fontname="Helvetica" style="${styleType['normalEmbeddedApp']}"  label="Embedded app stage" fillcolor="${colorType['normalEmbeddedApp']}"]
          ranking [shape="${shapeType['normalScoredRanking']}" color="${borderColourType['normalScoredRanking']}" fontcolor="${fontColorType['normalScoredRanking']}" fontname="Helvetica" style="${styleType['normalScoredRanking']}" label="Scored Ranking stage" fillcolor="${colorType['normalScoredRanking']}"]
          response [shape="${shapeType['normalScoredResponse']}" color="${borderColourType['normalScoredResponse']}" fontcolor="${fontColorType['normalScoredResponse']}" fontname="Helvetica" style="${styleType['normalScoredResponse']}" label="Scored Response stage" fillcolor="${colorType['normalScoredResponse']}"]
          traditionalWithMethods [shape="${shapeType['normalTraditional']}" color="${borderColourType['normalTraditional']}" fontcolor="${fontColorType['normalTraditional']}" fontname="Helvetica" style="${styleType['normalTraditional']}" label="Stage with screening methods (gradient fill)" fillcolor="${methodsGradientStart}:${colorType['normalTraditional']}"]
          traditional [shape="${shapeType['normalTraditional']}" color="${borderColourType['normalTraditional']}" fontcolor="${fontColorType['normalTraditional']}" fontname="Helvetica" style="${styleType['normalTraditional']}" label="Traditional stage" fillcolor="${colorType['normalTraditional']}"]
          unconfigured [shape="${shapeType['notExist']}" color="${borderColourType['notExist']}" fontcolor="${fontColorType['notExist']}" fontname="Helvetica" style="${styleType['notExist']}" label="Unconfigured stage" fillcolor="${colorType['notExist']}"]
          uninitilized [shape="${shapeType['startConversation']}" color="${borderColourType['startConversation']}" fontcolor="${fontColorType['startConversation']}"  fontname="Helvetica" style="${styleType['startConversation']}"  label="Uninitialized stage" fillcolor="${colorType['startConversation']}"]
      }`
    }
  },

  mounted () {
    // Calling addPanZoom first time
    this.addPanZoom()

    // A new conversation/journey starts as a lone uninitialized stage. When opening an existing one,
    // show nothing but "Drawing graph…" until its real graph is ready.
    if (!this.$route.query.app) {
      const uninitializedStage = 'uninitialized' + this.addVisualAttributesToNode({id: 'uninitialized', displayName: 'uninitialized', nature: 'notExist'})
      this.dots = 'digraph ""{' + graphDefaults + uninitializedStage + '}'
      this.showingPlaceholder = true
      this.redraw()
    }
  },

  methods: {
    // Drawn the first time it's opened, not on every page load
    drawLegend () {
      if (this.legendDrawn) return
      this.legendDrawn = true
      layoutGraph(this.legend)
        .then(graph => { document.getElementById('conversationLegend').innerHTML = graph })
        .catch(Exp => {
          this.legendDrawn = false
          console.error('legend', Exp)
        })
    },
    addPanZoom () {
      // Mouse  in out with Panzoom library
      const svgTree = document.getElementById('conversationTreeContainer')
      const zoomInBtn = document.getElementById('zoomin')
      const zoomOutBtn = document.getElementById('zoomout')
      this.panzoom = Panzoom(svgTree, {
        canvas: true,
        // Default 0.125 is too close for big journeys to fit on screen
        minScale: 0.04
      })
      // The "Double-click to open" tip points at a stage; once the graph moves it points at nothing
      svgTree.addEventListener('panzoomchange', this.hideClickHint)
      zoomInBtn.addEventListener('click', this.panzoom.zoomIn)
      zoomOutBtn.addEventListener('click', this.panzoom.zoomOut)
      // enable mouse wheel
      const parent = svgTree.parentElement
      parent.addEventListener('wheel', this.panzoom.zoomWithWheel)
    },
    validateConversation () {
      this.$emit('validateConversation')
    },
    // Same reset-and-refit the option/traffic toggles do. A new page has no config to draw yet.
    fitToScreen () {
      if (this.conversationObject && this.conversationObject.config) {
        this.renderConversation(this.conversationObject, true)
      } else {
        this.panzoom.reset()
      }
    },
    handleLeftClick (event) {
      const stageName = this.getStageNameIfNodeClicked(event)
      if (stageName) {
        this.$emit('nodeClick', stageName)
      }
    },
    handleOptionLabelToggle () {
      this.toggleTrafficSwitch = false
      this.renderConversation(this.conversationObject, true)
    },
    async handleMessagesToggle () {
      if (this.toggleMessagesSwitch && !this.functionAliases) await this.getFunctionAliases()
      if (this.toggleMessagesSwitch && !this.methodNames) await this.getMethodNames()
      this.renderConversation(this.conversationObject, true)
    },
    async getFunctionAliases () {
      try {
        const response = await api.buildRequest('functions', 'GET')
        this.functionAliases = (response.data.payload || []).reduce((map, fn) => {
          map[fn.function_name] = fn.alias
          return map
        }, {})
      } catch (error) {
        // Fall back to the function names
        this.functionAliases = {}
      }
    },
    async getMethodNames () {
      try {
        const response = await api.buildRequest('getMethodList', 'POST')
        this.methodNames = (response.data.payload || []).reduce((map, method) => {
          map[method.shortname] = method.name
          return map
        }, {})
      } catch (error) {
        // Fall back to the shortnames
        this.methodNames = {}
      }
    },
    async handleTrafficToggle () {
      this.toggleOptionLabelSwitch = false
      if (this.toggleTrafficSwitch) await this.getTrafficData()
      this.renderConversation(this.conversationObject, true)
    },
    async getTrafficData () {
      try {
        this.processing = true
        let params = {name: `GENERIC_SMS_APP###${this.conversationObject.name}`, type: this.conversationType}
        const response = await api.buildRequest('getTrafficData', 'GET', params)
        this.trafficData = response.data.payload
        // find the total number of users who started the conversation (count of all transitions out of uninitialized, including stalls at uninitialized)
        // this will be used later to scale to a total of 100
        this.totalUserCount = this.trafficData.filter(item => item.stage_name === 'uninitialized').reduce((sum, record) => sum + Number(record.count), 0)
        this.processing = false
      } catch (error) {
        this.processing = false
        return this.$swal({
          title: 'Failure',
          type: 'error',
          text: error.response ? error.response.data.payload.message : 'An error occurred'
        })
      }
    },
    rememberPointer (event) {
      this.pointerDown = { x: event.clientX, y: event.clientY }
    },
    // A single click on a stage only points out the double-click. Wait a moment first, so a
    // double-click doesn't flash the tip. Dragging the canvas also ends in a click: ignore that.
    handleStageClick (event) {
      const moved = this.pointerDown && Math.abs(event.clientX - this.pointerDown.x) + Math.abs(event.clientY - this.pointerDown.y) > 4
      if (moved || event.detail > 1 || !this.getStageNameIfNodeClicked(event)) return
      clearTimeout(this.clickHintTimer)
      this.clickHint = null
      const box = this.$el.getBoundingClientRect()
      const at = { x: event.clientX - box.left + 12, y: event.clientY - box.top + 12 }
      this.clickHintTimer = setTimeout(() => {
        this.clickHint = at
        this.clickHintTimer = setTimeout(() => { this.clickHint = null }, 1800)
      }, 300)
    },
    hideClickHint () {
      clearTimeout(this.clickHintTimer)
      this.clickHint = null
    },
    handleStageDoubleClick (event) {
      this.hideClickHint()
      const stageName = this.getStageNameIfNodeClicked(event)
      if (!stageName) return
      // A double-click also selects a word of the stage text
      if (window.getSelection) window.getSelection().removeAllRanges()
      this.$emit('editStage', stageName)
    },
    handleRightClick (event) {
      const stageName = this.getStageNameIfNodeClicked(event)
      if (stageName) {
        const contextMenu = this.$parent.$refs.treeContextMenu
        contextMenu.showMenu(event, stageName)
        this.placeContextMenu(document.getElementById(contextMenu.elementId), event)
      }
    },
    // The menu plugin places the menu with page coordinates but decides whether to flip it upwards
    // with the window height, so on a scrolled page it opened a row too high and the pointer landed
    // between Edit and Delete. Pin it (position: fixed in the theme) so the pointer sits on Edit.
    placeContextMenu (menu, event) {
      if (!menu) return
      const inset = 8
      const width = menu.offsetWidth
      const height = menu.offsetHeight
      const left = event.clientX - inset + width > window.innerWidth ? event.clientX - width + inset : event.clientX - inset
      const top = event.clientY - inset + height > window.innerHeight ? event.clientY - height + inset : event.clientY - inset
      menu.style.left = `${Math.max(4, left)}px`
      menu.style.top = `${Math.max(4, top)}px`
    },
    getStageNameIfNodeClicked (event) {
      let nodeClicked = this.findIfnodeWasClicked(event)
      if (nodeClicked) {
        return nodeClicked
      }
    },

    // If there is any change in the object, we have to re-render the visual tree.
    // Layout runs in a background worker (see graphLayout.js), so the page stays usable meanwhile.
    redraw (initialRendering = false) {
      const drawId = ++this.drawId
      clearTimeout(this.drawingTimer)
      // Only show "Drawing graph…" if it takes long enough to notice
      this.drawingTimer = setTimeout(() => {
        if (drawId === this.drawId) this.drawingVisible = true
      }, 300)
      const done = () => {
        clearTimeout(this.drawingTimer)
        this.drawingVisible = false
      }
      layoutGraph(this.dots)
        .then(graph => {
          if (drawId !== this.drawId) return
          done()
          this.layoutError = false
          this.showGraph(graph, initialRendering)
        })
        .catch(Exp => {
          // A newer drawing replaced this one
          if (Exp && Exp.superseded) return
          if (drawId !== this.drawId) return
          done()
          this.layoutError = true
          console.error('redraw', Exp)
        })
    },
    showGraph (graph, initialRendering) {
      try {
        // Reset the view only now the new graph is here. Resetting when the toggle was clicked showed
        // the old graph at full size in the wrong place while the new one was still being laid out.
        if (initialRendering) this.panzoom.reset({ animate: false })
        document.getElementById('conversationTreeSvg').innerHTML = graph
        this.highlightSelectedStage()

        if (initialRendering) this.fitGraph()
      } catch (Exp) {
        console.error('redraw', Exp)
      }
    },

    // Zoom out until the whole graph fits, and centre it. Uses the scale Panzoom actually applied:
    // it won't go below minScale, and dividing by the wanted (smaller) factor instead threw big
    // graphs off to one side.
    fitGraph () {
      const canvas = this.$el
      const container = document.getElementById('conversationTreeContainer')
      const svg = document.getElementById('conversationTreeSvg')
      if (!canvas || !container || !svg || !svg.offsetWidth || !svg.offsetHeight) return
      const wanted = Math.min(canvas.clientWidth / svg.offsetWidth, canvas.clientHeight / svg.offsetHeight)
      if (wanted >= 1) return
      this.panzoom.zoom(wanted, { animate: false })
      const scale = this.panzoom.getScale()
      // Panzoom draws the container as scale(s) translate(x, y) around its own centre. Solve for the
      // translate that puts the graph's centre on the canvas centre. offsetLeft/Top ignore transforms.
      const originX = container.offsetLeft + container.offsetWidth / 2
      const originY = container.offsetTop + container.offsetHeight / 2
      const graphX = container.offsetLeft + svg.offsetLeft + svg.offsetWidth / 2
      const graphY = container.offsetTop + svg.offsetTop + svg.offsetHeight / 2
      this.panzoom.pan(
        (canvas.clientWidth / 2 - originX) / scale - (graphX - originX),
        (canvas.clientHeight / 2 - originY) / scale - (graphY - originY),
        { animate: false }
      )
    },

    // Graphwiz DOT node names can only have _ but NEVER -. So, we hve to wrap the name as string and then pass
    makeStageNameDotFriendly (str) {
      return `"${str}"`
    },

    getPenWidth (transitionData) {
      let penwidth = 2
      if (transitionData) penwidth = Number(transitionData['count']) / this.totalUserCount * 100
      return penwidth
    },

    // Every node in the drawn graph, with its stage name (Graphviz puts the node name in <title>)
    graphNodes () {
      const svg = document.getElementById('conversationTreeSvg')
      if (!svg) return []
      return Array.from(svg.querySelectorAll('g.node')).map(el => {
        const title = el.querySelector('title')
        return { el, name: title ? title.textContent : '' }
      }).filter(node => node.name)
    },
    // The selected stage is marked with a CSS class instead of a bigger, double-bordered node.
    // Changing the DOT meant laying out the whole graph again on every stage switch, which is slow
    // on big journeys and made the other stages jump around.
    highlightSelectedStage () {
      this.graphNodes().forEach(node => {
        node.el.classList.toggle('nv-node--selected', node.name === this.selectedStageName)
      })
    },
    openStageSearch () {
      this.stageSearchOpen = true
      // PF aliases make PFs searchable by the name people see in the editor
      if (!this.functionAliases) this.getFunctionAliases()
    },
    closeExportMenu (event) {
      if (!this.$el.querySelector('.canvas-export').contains(event.relatedTarget)) this.exportMenuOpen = false
    },
    // Downloads the graph as currently drawn (with messages, options or traffic if they're on)
    async exportGraph (format) {
      this.exportMenuOpen = false
      const svgEl = document.querySelector('#conversationTreeSvg svg')
      if (!svgEl) return
      const clone = svgEl.cloneNode(true)
      clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
      clone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink')
      clone.querySelectorAll('.nv-node--selected, .nv-node--found').forEach(el => el.classList.remove('nv-node--selected', 'nv-node--found'))
      const svgText = new XMLSerializer().serializeToString(clone)
      const fileName = ((this.conversationObject && this.conversationObject.name) || this.conversationType).replace(/[^\w-]+/g, '_')
      if (format === 'svg') {
        this.downloadBlob(new Blob([svgText], { type: 'image/svg+xml' }), `${fileName}.svg`)
        return
      }
      if (format === 'html') {
        const exportedAt = new Date().toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
        const html = buildGraphHtml({
          title: (this.conversationObject && this.conversationObject.name) || this.conversationType,
          subtitle: `${this.conversationType} · exported ${exportedAt}`,
          svgText
        })
        this.downloadBlob(new Blob([html], { type: 'text/html' }), `${fileName}.html`)
        return
      }
      this.exporting = true
      try {
        const box = svgEl.getBoundingClientRect()
        const width = svgEl.width.baseVal.value || box.width
        const height = svgEl.height.baseVal.value || box.height
        // 2x for sharp text, but stay inside the browser's canvas size limit for big journeys
        const scale = Math.min(2, 16000 / Math.max(width, height), Math.sqrt(200000000 / (width * height)))
        const image = await new Promise((resolve, reject) => {
          const img = new Image()
          img.onload = () => resolve(img)
          img.onerror = reject
          img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgText)
        })
        const canvas = document.createElement('canvas')
        canvas.width = Math.round(width * scale)
        canvas.height = Math.round(height * scale)
        const ctx = canvas.getContext('2d')
        ctx.fillStyle = '#FFFFFF'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height)
        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'))
        if (!blob) throw new Error('empty image')
        this.downloadBlob(blob, `${fileName}.png`)
      } catch (error) {
        this.$swal({ title: 'Export failed', type: 'error', text: 'This graph is too large for a PNG. Try SVG instead.' })
      } finally {
        this.exporting = false
      }
    },
    downloadBlob (blob, fileName) {
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      link.remove()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    },
    // Every language's text of a stage, for searching
    getAllStageText (textArray) {
      if (!Array.isArray(textArray)) return ''
      const parts = []
      textArray.forEach(item => {
        if (typeof item === 'string') {
          parts.push(item)
        } else if (item && typeof item === 'object') {
          Object.keys(item).filter(key => key !== 'properties').map(key => item[key]).forEach(loc => {
            if (loc && typeof loc === 'object') Object.values(loc).forEach(v => { if (typeof v === 'string') parts.push(v) })
          })
        }
      })
      return parts.join('\n')
    },
    // A short piece of text around the first matching word
    searchSnippet (text, words) {
      const lower = text.toLowerCase()
      const word = words.find(w => lower.indexOf(w) !== -1)
      if (!word) return null
      const at = lower.indexOf(word)
      const start = Math.max(0, at - 25)
      const end = Math.min(text.length, at + word.length + 40)
      const flat = value => value.replace(/\s+/g, ' ')
      return {
        before: (start > 0 ? '…' : '') + flat(text.slice(start, at)),
        match: flat(text.slice(at, at + word.length)),
        after: flat(text.slice(at + word.length, end)) + (end < text.length ? '…' : '')
      }
    },
    closeStageSearch () {
      setTimeout(() => { this.stageSearchOpen = false }, 100)
    },
    moveStageResult (step) {
      if (!this.stageResults.length) return
      this.stageResultIndex = (this.stageResultIndex + step + this.stageResults.length) % this.stageResults.length
    },
    // Centre the canvas on the stage, flash it, then open it the same way right-click > Edit does
    goToStage (name) {
      if (!name) return
      this.stageQuery = ''
      this.stageSearchOpen = false
      const node = this.graphNodes().find(n => n.name === name)
      const canvas = this.$el
      if (node && canvas) {
        const nodeBox = node.el.getBoundingClientRect()
        const canvasBox = canvas.getBoundingClientRect()
        const scale = this.panzoom.getScale()
        this.panzoom.pan(
          (canvasBox.left + canvasBox.width / 2 - (nodeBox.left + nodeBox.width / 2)) / scale,
          (canvasBox.top + canvasBox.height / 2 - (nodeBox.top + nodeBox.height / 2)) / scale,
          { relative: true, animate: true }
        )
        node.el.classList.remove('nv-node--found')
        // Restart the flash animation if the same stage is found twice in a row
        void node.el.getBoundingClientRect()
        node.el.classList.add('nv-node--found')
        setTimeout(() => node.el.classList.remove('nv-node--found'), 1600)
      }
      this.$emit('editStage', name)
    },

    // Define how the node will look. This can get fancier!
    addVisualAttributesToNode (stage) {
      let tooltip = `Stage: ${stage.displayName}\n`
      let showGradient = false

      if (this.trafficStalledStages.includes(stage.id)) {
        // outline the nodes with the number of users who are currently stopped there
        // note that for now this will include both users who stalled and users who are waiting for a scheduled message
        let transitionData = this.trafficData.find(item => item['stage_name'] === stage.id && !item['next_stage'])
        tooltip += `Stalled user(s): ${transitionData ? transitionData['count'] : '0'}`
        return `[shape="${shapeType[stage.nature]}" tooltip="${tooltip}" penwidth="${this.getPenWidth(transitionData)}" color="${borderColourType['stalledTraffic']}" fontcolor="${fontColorType[stage.nature]}" fontname="Helvetica" style="${styleType['stalledTraffic']}" label="${this.getNodeLabel(stage)}" niviStageName="${stage.id}" fillcolor="${colorType[stage.nature]}" peripheries="${shapePeripheries.otherStage}" fontsize="${fontSizeType.otherStage}"]`
      }
      if (stage.stageValue && stage.stageValue.methods && Object.keys(stage.stageValue.methods).length > 0) {
        tooltip += 'Methods: (Format = Option Name: action : offerings) \n'
        for (const [key, value] of Object.entries(stage.stageValue.methods)) {
          tooltip += `${key.charAt(0) + key.slice(1)}: ${value.type} : ${value.methods.join(', ')}\n`
        }
        showGradient = true
      }
      return `[shape="${shapeType[stage.nature]}" tooltip="${tooltip}" color="${borderColourType[stage.nature]}" fontcolor="${fontColorType[stage.nature]}" fontname="Helvetica" style="${styleType[stage.nature]}" label="${this.getNodeLabel(stage)}" niviStageName="${stage.id}" fillcolor="${showGradient ? methodsGradientStart + ':' : ''}${colorType[stage.nature]}" peripheries="${shapePeripheries.otherStage}" fontsize="${fontSizeType.otherStage}"]`
    },
    // Stage name, plus (with the Messages toggle on) the stage's message and its processing function
    getNodeLabel (stage) {
      if (!this.toggleMessagesSwitch || !stage.stageValue) return stage.displayName
      const value = stage.stageValue
      let label = stage.displayName + '\\n'
      // Every message in the stage, in full; numbered when there's more than one
      const messages = this.getStageMessages(value.text)
      messages.forEach((message, index) => {
        if (messages.length > 1) label += `${index ? '\\n' : ''}\\nMessage ${index + 1}:\\l`
        label += (messages.length > 1 ? '' : '\\n') + this.wrapForNode(message, 45)
      })
      this.getStageDetails(value).forEach(line => {
        label += '\\n' + this.wrapForNode(line, 45)
      })
      const pf = value.processingFunctionConfig
      if (pf && pf.functionName) {
        const input = pf.inputConfig
        const inputText = input ? (typeof input === 'string' ? input : JSON.stringify(input)) : ''
        const pfName = (this.functionAliases && this.functionAliases[pf.functionName]) || pf.functionName
        label += '\\n' + this.wrapForNode(`PF: ${pfName}${inputText ? ' (' + inputText + ')' : ''}`, 45)
      }
      return label
    },
    // What the user is asked to reply with, in the words of the stage editor's Input Type
    getUserInputLabel (value) {
      const type = value.validations && value.validations.type
      if (!type) return ''
      if (type === 'Number') {
        // Number with options is picking a button (or typing its number)
        const hasOptions = (Array.isArray(value.options) && value.options.length) || JSON.stringify(value.text || '').indexOf('{{') !== -1
        return hasOptions ? 'Button' : 'Number'
      }
      const labels = {
        Boolean: 'Yes / No',
        Text: 'Text',
        DynamicNumber: 'Dynamic list',
        whatsapp_flows: 'WhatsApp Flow',
        nlpDecision: 'NLP question'
      }
      return labels[type] || type
    },
    // Other settings of the stage worth seeing on the graph, one line each
    getStageDetails (value) {
      const lines = []
      const list = items => (Array.isArray(items) ? items : [items]).filter(Boolean).join(', ')
      const methodName = key => (this.methodNames && this.methodNames[key]) || key
      const delayText = delay => delay && delay.quantity ? `${Number(delay.quantity) || delay.quantity} ${delay.units || ''}`.trim() : ''
      if (Array.isArray(value.recommendation) && value.recommendation.length) {
        lines.push(`Recommendation Set: ${value.recommendation.map(methodName).join(', ')}`)
      }
      // Per option: add or remove methods
      if (value.methods && typeof value.methods === 'object') {
        Object.keys(value.methods).forEach(option => {
          const config = value.methods[option] || {}
          const methods = Array.isArray(config.methods) ? config.methods.map(methodName).join(', ') : ''
          lines.push(`On "${option}": ${config.type || 'methods'}${methods ? ' ' + methods : ''}`)
        })
      }
      const delay = delayText(value.delay)
      const sendTime = value.sendTime && value.sendTime.hours ? `${value.sendTime.hours}:${value.sendTime.minutes || '00'}` : ''
      if (delay || sendTime) lines.push(`Delay: ${[delay, sendTime && 'at ' + sendTime].filter(Boolean).join(' ')}`)
      if (value.msgTemplateName) lines.push(`Nudge: ${value.msgTemplateName}`)
      const followups = value.followupNudges && Array.isArray(value.followupNudges.delays) ? value.followupNudges.delays : []
      followups.forEach((nudge, index) => {
        const after = delayText(nudge)
        lines.push(`Follow-up nudge ${index + 1}: ${nudge.msg_template_name || '-'}${after ? ' after ' + after : ''}`)
      })
      if (Array.isArray(value.scheduledAppKeyword) && value.scheduledAppKeyword.length) {
        lines.push(`Scheduled keyword: ${value.scheduledAppKeyword.map(k => String(k).replace(/^conversationkeyword:/, '')).join(', ')}`)
      }
      const userInput = this.getUserInputLabel(value)
      if (userInput) lines.push(`User Input: ${userInput}`)
      if (value.failed) lines.push(`Wrong input goes to: ${value.failed}`)
      if (value.nextApp) lines.push(`Next app: ${String(value.nextApp).split('###').pop()}`)
      if (value.subsequentApp) lines.push(`Then: ${String(value.subsequentApp).split('###').pop()}`)
      if (value.media) lines.push(`Media: ${typeof value.media === 'object' ? (value.media.name || value.media.fileName || value.media.url || 'attached') : value.media}`)
      if (typeof value.notification === 'string' && value.notification) lines.push(`Notification: ${value.notification}`)
      if (Array.isArray(value.tags) && value.tags.length) lines.push(`Tags: ${list(value.tags.map(tag => (tag && typeof tag === 'object') ? (tag.label || tag.id) : tag))}`)
      return lines
    },
    // A stage's text is a list of messages, each { country: { lang: text }, properties }.
    // Returns each message's English text (or the first language it has).
    getStageMessages (textArray) {
      if (!Array.isArray(textArray)) return []
      const messages = []
      textArray.forEach(item => {
        let text = ''
        if (typeof item === 'string') {
          text = item
        } else if (item && typeof item === 'object') {
          const locales = Object.keys(item).filter(key => key !== 'properties' && item[key] && typeof item[key] === 'object').map(key => item[key])
          const withEnglish = locales.find(loc => typeof loc.eng === 'string' && loc.eng.trim())
          text = withEnglish ? withEnglish.eng : (locales.map(loc => Object.values(loc).find(v => typeof v === 'string' && v.trim())).find(Boolean) || '')
        }
        text = text.replace(/\n{2,}/g, '\n').trim()
        if (text) messages.push(text)
      })
      return messages
    },
    // Wraps text into left-aligned Graphviz label lines
    wrapForNode (text, width) {
      const clean = text.replace(/\\/g, '').replace(/\r/g, '')
      const lines = []
      clean.split('\n').forEach(paragraph => {
        let line = ''
        paragraph.split(/\s+/).filter(Boolean).forEach(word => {
          while (word.length > width) {
            if (line) { lines.push(line); line = '' }
            lines.push(word.slice(0, width))
            word = word.slice(width)
          }
          if (line && (line + ' ' + word).length > width) {
            lines.push(line)
            line = word
          } else {
            line = line ? line + ' ' + word : word
          }
        })
        if (line) lines.push(line)
      })
      return lines.map(line => line.replace(/"/g, '\\"') + '\\l').join('')
    },
    addArrowProperties (stageNature, optionName, stage, nextStage, inputLabel) {
      if (!this.toggleTrafficSwitch) {
        const label = (optionName && this.toggleOptionLabelSwitch) ? `label="${(inputLabel ? inputLabel + '\n' : '') + this.getStageDisplayName(optionName)}"` : (inputLabel ? `label="${inputLabel}"` : '')
        return `[arrowhead="${arrowShape[stageNature]}" tooltip="${optionName || ' '}" fontname="Helvetica" penwidth="1" ${label}]`
      } else {
        // Update each edge based on counts
        // as implemented here, if three different answers lead from node X to node Y, the count gets applied to each of them
        // it would be cleaner for this visualization to squash those three edges down to one edge
        // fall back to thin dotted line if the path exists but no users have walked it
        let edgeStyle = 'dotted'
        let transitionData = this.trafficData.find(item => item['stage_name'] === stage && item['next_stage'] === nextStage)
        if (transitionData) {
          edgeStyle = 'tapered'  // this shows the direction of the transition
        }
        let tooltip = `Total user(s): ${transitionData ? transitionData['count'] : '0'}`
        console.log('tooltip: ', tooltip)
        return `[style="${edgeStyle}" tooltip="${tooltip}" label="${transitionData ? transitionData['count'] : '0'}" fontcolor="#0B7C72" color="#1E3A5F" arrowhead="none" fontname="Helvetica-bold" penwidth="${this.getPenWidth(transitionData)}"]`
      }
    },
    getStageDisplayName (stageName) {
      let displayNameArray = stageName.match(/.{1,30}/g)
      displayNameArray = displayNameArray.map(item => item + '\\n')
      // Removing last line break, because it is unnecessary and creates issue when there is no stageType given for older conversation
      return displayNameArray.join('').replace(/\\n$/, '')
    },
    getTrimmedName (stage, stageNode) {
      let displayName = this.getStageDisplayName(stage)

      // For embdeed app, add app name in the display name
      if (stageNode.stageType === stageTypes.embeddedConversation && stageNode.conversation) {
        let conversationName = this.getStageDisplayName(stageNode.conversation)
        if (stageNode.conversation.includes('###')) {
          const appDetails = stageNode.conversation.split('###')
          conversationName = this.getStageDisplayName(appDetails[1])
          displayName = displayName + (conversationName ? ('\\nApp: ' + conversationName) : '')
        } else {
          displayName = displayName + '\\nApp: ' + stageNode.conversation
        }
      // Fod JoR delay stage, add delay in the stage name
      } else if (stageNode.stageType === stageTypes.journeyReminder && stageNode.joRDelayConfig && stageNode.joRDelayConfig.delay) {
        displayName = displayName + '\\nDelay: ' + stageNode.joRDelayConfig.delay.quantity + ' ' + stageNode.joRDelayConfig.delay.units
      }

      return displayName
    },
    getStageNature (stage) {
      let nature = 'notExist'
      if (stage) {
        if (stage.nextApp) {
          return 'endConversation'
        }
        if ((stage.stageName && stage.stageName === 'uninitialized') || (stage.displayName && stage.displayName === 'uninitialized')) {
          return 'startConversation'
        }
        if (stage.processingFunctionConfig) {
          return 'normalConditionalBranching'
        }
        if (stage.optionConfig) {
          for (let i = 0; i < stage.optionConfig.length; i++) {
            if (stage.optionConfig[i].for === 'drillDownReferrals') {
              return 'normalDrillDown'
            }
          }
        }
        if (stage.stageType === stageTypes.traditional) {
          return 'normalTraditional'
        } else if (stage.stageType === stageTypes.scoredResponse) {
          return 'normalScoredResponse'
        } else if (stage.stageType === stageTypes.scoreRanking) {
          return 'normalScoredRanking'
        } else if (stage.stageType === stageTypes.embeddedConversation) {
          return 'normalEmbeddedApp'
        } else if (stage.stageType === stageTypes.fbOptIn) {
          return 'normalFbOptin'
        } else if (stage.stageType === stageTypes.journeyReminder) {
          return 'normalJoR'
        }
        // In older conversations, stageName attribute is not present. So, deriving from displayName created from 'stage' as key
      }
      return nature
    },
    /*
    Reads the nivi convo json and converts it to digraph with node definitions first and then the relations
    */
    getStageInputLabel (stage, stageData, index) {
      if (stageData.type === 'questions' && stageData.validations && stageData.validations.type === 'Number' && !stageData.processingFunctionConfig) {
        return 'Input: ' + (index + 1)
      }
    },
    renderConversation (convo, initialRendering = false) {
      let data = JSON.parse(JSON.stringify(convo))

      // If convo type is 'journeyReminder' then sort the JOR stages based on delay timing
      if (convo.config.type === stageTypes.journeyReminder) {
        data = sortStagesForJor(JSON.parse(JSON.stringify(convo)))
      } else {
        data.config.stages = new Map(Object.entries(convo.config.stages || {}))
      }

      let allStages = [] // holds the attributes for all stages fomr the nivi conversation.
      const unconfiguredAdded = new Set() // stages that are only referenced; draw each once
      const edgesAdded = new Set() // for the traffic view, which draws one edge per pair
      let digraphString = '' // stirng that defines the relaton between nodes
      let allStageNames = '' // all stages will have to be defined, before relaitons are created between them. this string will hold that data.
      this.trafficStalledStages = []
      for (let [stage, stageValue] of data.config.stages) {
        if (!stageValue) continue
        let stageNode = JSON.parse(JSON.stringify(stageValue))
        const displayName = this.getTrimmedName(stage, stageNode)
        // In older conversations, stageType is not present, defaulting to 'traditional'
        const stageType = (stageNode.stageType ? stageNode.stageType : stageTypes.traditional)
        stageNode = {
          ...stageNode,
          stageType,
          displayName
        }
        let attrs = {
          digraphNodeName: this.makeStageNameDotFriendly(stage),
          stageType,
          id: stage,
          displayName,
          stageValue: stageValue,
          nature: this.getStageNature(stageNode)
        }
        if (stageNode && stageNode.next) {
          if (typeof stageNode.next === 'string') {
            stageNode.next = [stageNode.next]
          }
          if (typeof stageNode.next === 'object') {
            stageNode.next.forEach((s, index) => {
              if (!data.config.stages.get(s) && !unconfiguredAdded.has(s)) {
                unconfiguredAdded.add(s)
                allStages.push({
                  digraphNodeName: this.makeStageNameDotFriendly(s),
                  stageType: '',
                  id: s,
                  nature: 'notExist',
                  displayName: this.getStageDisplayName(s)
                })
              }

              // For Traffic toggle on, don't add duplicate edges
              const edgeKey = `${stage}\u0000${s}`
              if (!this.toggleTrafficSwitch || !edgesAdded.has(edgeKey)) {
                edgesAdded.add(edgeKey)
                digraphString += `${this.makeStageNameDotFriendly(stage)}->${this.makeStageNameDotFriendly(s)}${this.addArrowProperties(this.getStageNature(data.config.stages.get(s)), stageNode.options && stageNode.options[index], stage, s, this.getStageInputLabel(stage, stageValue, index))}\n`
              }
            })
          }

          // Find out on how many stages, user got stalled
          if (this.toggleTrafficSwitch && this.trafficData.find(item => item['stage_name'] === stageNode.stageName && !item['next_stage'])) {
            this.trafficStalledStages.push(stageNode.stageName)
          }
        }
        allStages.push(attrs)
      }
      let uninitializedStage = allStages.find(o => o.id === 'uninitialized')

      // set the unitialized node first then enter other nodes
      allStageNames = 'uninitialized' + this.addVisualAttributesToNode(uninitializedStage) + '\n'
      allStages.forEach(stage => {
        if (stage.id !== 'uninitialized') {
          allStageNames += stage.digraphNodeName + this.addVisualAttributesToNode(stage) + '\n'
        }
      })
      this.dots = 'digraph ""{\n' + graphDefaults + allStageNames + digraphString + '\n}'
      // From the journey itself, so Find stage works even while the graph is drawing (or failed to)
      this.stageSearchIndex = allStages.map(item => {
        const value = item.stageValue || {}
        const pf = value.processingFunctionConfig && value.processingFunctionConfig.functionName
        return { name: item.id, message: this.getAllStageText(value.text), pf: pf || '', details: this.getStageDetails(value).join('\n') }
      })
      // Don't leave the starting placeholder on screen while the real graph draws
      if (this.showingPlaceholder) {
        this.showingPlaceholder = false
        document.getElementById('conversationTreeSvg').innerHTML = ''
      }
      this.redraw(initialRendering)
    },

    findIfnodeWasClicked (ev) {
      let nodeClicked = null
      var path = ev.path || (ev.composedPath && ev.composedPath())

      path.every(p => {
        try {
          if (p.id && p.id.indexOf('node') === 0) {
            /*
            get the stage name from the node's title
            */
            if (p.children[0].innerHTML) {
              nodeClicked = p.children[0].innerHTML
              return false
            }
          }
          return true
        } catch (err) {
          console.log('ERROR', err)
        }
      })
      return nodeClicked
    }
  },

  beforeDestroy () {
    clearTimeout(this.drawingTimer)
    clearTimeout(this.clickHintTimer)
  },
  watch: {
    showLegend (isShown) {
      if (isShown) this.drawLegend()
    },
    stageQuery () {
      this.stageResultIndex = 0
      this.stageSearchOpen = true
    },
    conversationObject: function (value) {
      this.renderConversation(value, this.isFirstTimeRender)
      this.isFirstTimeRender = false
    },
    selectedStageName: function () {
      this.highlightSelectedStage()
    }
  },
  computed: {
    stageResults () {
      const words = this.stageQuery.trim().toLowerCase().split(/\s+/).filter(Boolean)
      if (!words.length) return []
      const aliases = this.functionAliases || {}
      const matches = []
      this.stageSearchIndex.forEach(stage => {
        const pf = stage.pf ? `${aliases[stage.pf] || ''} ${stage.pf}`.trim() : ''
        const haystack = `${stage.name}\n${stage.message}\n${pf}\n${stage.details}`.toLowerCase()
        if (!words.every(w => haystack.indexOf(w) !== -1)) return
        const name = stage.name.toLowerCase()
        if (words.every(w => name.indexOf(w) !== -1)) {
          matches.push({ name: stage.name, rank: 0 })
        } else if (stage.message && words.some(w => stage.message.toLowerCase().indexOf(w) !== -1)) {
          matches.push({ name: stage.name, rank: 1, where: 'Message', snippet: this.searchSnippet(stage.message, words) })
        } else if (pf && words.some(w => pf.toLowerCase().indexOf(w) !== -1)) {
          matches.push({ name: stage.name, rank: 2, where: 'PF', snippet: this.searchSnippet(pf, words) })
        } else {
          matches.push({ name: stage.name, rank: 3, where: 'Setting', snippet: this.searchSnippet(stage.details, words) })
        }
      })
      // Stage-name matches first, then messages, then PFs
      return matches.sort((a, b) => a.rank - b.rank).slice(0, 20)
    },
    conversationType () {
      return this.isJourneyConversation ? 'Journey' : 'Conversation'
    }
  }
}
</script>
<style>
  .node {
    cursor:pointer
  }
  #conversationTreeSvg .node:hover {
    filter: drop-shadow(0 4px 10px rgba(30, 58, 95, 0.22));
  }
  .edge {
    cursor:pointer
  }
  /* Highlight the arrow under the pointer (CSS beats Graphviz's inline colours) */
  #conversationTreeSvg .edge:hover path { stroke: #0D9488; }
  #conversationTreeSvg .edge:hover polygon,
  #conversationTreeSvg .edge:hover ellipse { stroke: #0D9488; }
  #conversationTreeSvg .edge:hover polygon:not([fill="none"]),
  #conversationTreeSvg .edge:hover ellipse:not([fill="none"]) { fill: #0D9488; }
  #conversationTreeSvg .edge:hover text { fill: #0B7C72; }
  /* Selected stage: thick teal outline with a strong glow, so it stands out from every stage colour */
  #conversationTreeSvg .node.nv-node--selected > path,
  #conversationTreeSvg .node.nv-node--selected > polygon,
  #conversationTreeSvg .node.nv-node--selected > ellipse { stroke: #0D9488; stroke-width: 4px; }
  #conversationTreeSvg .node.nv-node--selected > text { font-weight: 700; }
  #conversationTreeSvg .node.nv-node--selected,
  #conversationTreeSvg .node.nv-node--selected:hover {
    filter: drop-shadow(0 0 3px rgba(13, 148, 136, 0.9)) drop-shadow(0 0 12px rgba(13, 148, 136, 0.55));
  }
  /* The end stage is teal already; give it a navy outline instead */
  #conversationTreeSvg .node.nv-node--selected > polygon[fill="#0D9488" i] { stroke: #1E3A5F; }
  /* Stage picked from Find stage: brief pulse */
  #conversationTreeSvg .node.nv-node--found { animation: nv-node-found 0.5s ease-in-out 3; }
  @keyframes nv-node-found {
    50% { filter: drop-shadow(0 0 14px rgba(13, 148, 136, 0.95)); }
  }
</style>
<style scoped>
  /* The canvas. Toolbars float over it; Panzoom ignores anything marked panzoom-exclude. */
  .container__left {
    position: relative;
    background-color: #FAFBFD;
    background-image: radial-gradient(rgba(30, 58, 95, 0.12) 1px, transparent 1px);
    background-size: 18px 18px;
  }
  .canvas-toolbar {
    position: absolute;
    top: 12px;
    z-index: 100;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .canvas-toolbar--left { left: 12px; }
  .canvas-toolbar--right { right: 12px; }
  .canvas-tool-group {
    display: flex;
    align-items: center;
    gap: 2px;
    padding: 4px;
    background: #FFFFFF;
    border: 1px solid #E2E8F0;
    border-radius: 10px;
    box-shadow: 0 1px 3px rgba(30, 58, 95, 0.1);
  }
  .canvas-icon-btn {
    width: 30px;
    height: 30px;
    border: none;
    border-radius: 7px;
    background: transparent;
    color: #1E3A5F;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.15s ease;
  }
  .canvas-icon-btn:hover { background: #EBF0F7; }
  .canvas-icon-btn:focus-visible,
  .canvas-btn:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.18); }
  .canvas-icon-btn--plain { width: 26px; height: 26px; color: #8896A5; }
  .canvas-status {
    position: absolute;
    top: 64px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 100;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 14px;
    border-radius: 999px;
    background: #FFFFFF;
    border: 1px solid #E2E8F0;
    box-shadow: 0 4px 16px rgba(30, 58, 95, 0.12);
    color: #1E3A5F;
    font-size: 13px;
    font-weight: 600;
  }
  .canvas-status .fa { color: #0D9488; }
  .canvas-error {
    position: absolute;
    top: 64px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 100;
    display: flex;
    gap: 12px;
    max-width: 420px;
    padding: 14px 16px;
    border-radius: 12px;
    background: #FFFFFF;
    border: 1px solid #F5C2C9;
    box-shadow: 0 8px 30px -4px rgba(29, 40, 58, 0.15);
    color: #2D3748;
    font-size: 13px;
  }
  .canvas-error .fa { color: #E05263; font-size: 18px; margin-top: 2px; }
  .canvas-error strong { color: #1E3A5F; }
  .canvas-error p { margin: 2px 0 0; color: #5C6E8A; }
  .canvas-search { position: relative; padding: 0; }
  .canvas-search__icon {
    position: absolute;
    left: 11px;
    top: 50%;
    transform: translateY(-50%);
    color: #8896A5;
    font-size: 12px;
    pointer-events: none;
  }
  .canvas-search__input {
    width: 180px;
    height: 38px;
    padding: 0 10px 0 30px;
    border: none;
    border-radius: 10px;
    background: transparent;
    color: #2D3748;
    font-size: 13px;
    outline: none;
    transition: width 0.15s ease, box-shadow 0.15s ease;
  }
  .canvas-search__input::placeholder { color: #8896A5; }
  .canvas-search__input:focus { width: 240px; box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.18); }
  .canvas-search__results {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    width: 340px;
    max-height: 380px;
    overflow-y: auto;
    margin: 0;
    padding: 6px;
    list-style: none;
    background: #FFFFFF;
    border: 1px solid #E2E8F0;
    border-radius: 10px;
    box-shadow: 0 8px 30px -4px rgba(29, 40, 58, 0.2);
  }
  .canvas-search__result {
    padding: 7px 10px;
    border-radius: 7px;
    color: #2D3748;
    font-size: 13px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: pointer;
  }
  .canvas-search__result--active { background: #E6F5F4; color: #0B7C72; }
  .canvas-search__name { overflow: hidden; text-overflow: ellipsis; }
  .canvas-search__snippet {
    margin-top: 2px;
    color: #5C6E8A;
    font-size: 12px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .canvas-search__snippet mark { padding: 0; background: #FDE68A; color: inherit; border-radius: 2px; }
  .canvas-search__where {
    display: inline-block;
    margin-right: 4px;
    padding: 0 5px;
    border-radius: 4px;
    background: #EEF2F6;
    color: #1E3A5F;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
  }
  .canvas-search__result--active .canvas-search__snippet { color: #0B7C72; }
  .canvas-export { position: relative; }
  .canvas-export__menu {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    z-index: 102;
    min-width: 170px;
    margin: 0;
    padding: 6px;
    list-style: none;
    background: #FFFFFF;
    border: 1px solid #E2E8F0;
    border-radius: 10px;
    box-shadow: 0 8px 30px -4px rgba(29, 40, 58, 0.2);
  }
  .canvas-export__menu button {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 7px 10px;
    border: none;
    border-radius: 7px;
    background: none;
    color: #2D3748;
    font-size: 13px;
    text-align: left;
  }
  .canvas-export__menu button:hover { background: #E6F5F4; color: #0B7C72; }
  .canvas-search__result--empty { color: #8896A5; cursor: default; white-space: normal; }
  .canvas-toggle {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    padding: 4px 8px;
    border-radius: 7px;
    font-size: 13px;
    font-weight: 600;
    color: #5C6E8A;
    cursor: pointer;
    user-select: none;
  }
  .canvas-toggle:hover { background: #F5F7FA; }
  .canvas-toggle input { position: absolute; opacity: 0; pointer-events: none; }
  .canvas-toggle__track {
    position: relative;
    width: 28px;
    height: 16px;
    border-radius: 999px;
    background: #CFD8E3;
    transition: background-color 0.15s ease;
  }
  .canvas-toggle__thumb {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #FFFFFF;
    box-shadow: 0 1px 2px rgba(30, 58, 95, 0.25);
    transition: transform 0.15s ease;
  }
  .canvas-toggle--on { color: #1E3A5F; }
  .canvas-toggle--on .canvas-toggle__track { background: #0D9488; }
  .canvas-toggle--on .canvas-toggle__thumb { transform: translateX(12px); }
  .canvas-btn {
    height: 38px;
    padding: 0 14px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid #E2E8F0;
    border-radius: 10px;
    background: #FFFFFF;
    color: #1E3A5F;
    font-size: 13px;
    font-weight: 600;
    box-shadow: 0 1px 3px rgba(30, 58, 95, 0.1);
    transition: background-color 0.15s ease, border-color 0.15s ease;
  }
  .canvas-btn:hover { background: #F5F7FA; }
  .canvas-btn--active { border-color: #0D9488; color: #0B7C72; background: #E6F5F4; }
  .canvas-btn--primary { background: #0D9488; border-color: #0D9488; color: #FFFFFF; box-shadow: 0 2px 8px -2px rgba(13, 148, 136, 0.3); }
  .canvas-btn--primary:hover { background: #0B7C72; border-color: #0B7C72; }
  .conversation-graph-legend {
    position: absolute;
    top: 60px;
    right: 12px;
    z-index: 101;
    max-height: calc(100% - 72px);
    overflow: auto;
    overscroll-behavior: contain;
    padding: 0 12px 12px;
    background: #FFFFFF;
    border: 1px solid #E2E8F0;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(30, 58, 95, 0.15);
  }
  .conversation-graph-legend__header {
    position: sticky;
    top: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 0 6px;
    background: #FFFFFF;
    font-weight: 700;
    color: #1E3A5F;
  }
  .canvas-hint {
    position: absolute;
    left: 12px;
    bottom: 10px;
    z-index: 100;
    padding: 4px 10px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.85);
    border: 1px solid #E2E8F0;
    color: #8896A5;
    font-size: 12px;
    pointer-events: none;
  }
  .canvas-click-hint {
    position: absolute;
    z-index: 103;
    padding: 5px 10px;
    border-radius: 8px;
    background: #1E3A5F;
    color: #FFFFFF;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    pointer-events: none;
    box-shadow: 0 4px 14px -4px rgba(29, 40, 58, 0.4);
    animation: canvas-click-hint-in 0.12s ease-out;
  }
  @keyframes canvas-click-hint-in {
    from { opacity: 0; transform: translateY(4px); }
    to { opacity: 1; transform: none; }
  }
  /* The old graph fades back while the new one is drawn */
  .conversation-tree-container--stale #conversationTreeSvg {
    opacity: 0.35;
    transition: opacity 0.15s ease;
  }
  .conversation-tree-container {
    display: flex;
    flex: 1;
    text-align: center;
    justify-content: top;
    zoom: 1;
    justify-content: center;
    white-space: pre;
    height: fit-content;
  }
  #conversationTreeSvg {
    height: fit-content;
    padding-top: 64px;
  }
</style>


// WEBPACK FOOTER //
// conversationTreeView.vue?e3f758b0